<template lang="pug">
v-container.pa-0(fluid)
  template(v-if="isFetching")
    v-row.row-height(no-gutters)
      v-col.pa-8
        v-skeleton-loader(type="list-item-three-line@4")
      v-col.pa-8.preview-background
        v-skeleton-loader(type="list-item-three-line@4")
  template(v-else)
    v-row(no-gutters)
      v-col.pa-8(cols="6")
        v-sheet.d-flex.align-center.yellow100.round-xl.pa-3.mb-4(v-if="hasReservationsOrPreorders" style="border: 1px solid #FC0 !important;")
          v-icon(size="20" color="danger500") $yzi-info-circle
          div.ml-3.flex-grow-1
            div.yz-regular-body-md {{ reservationPreorderBannerText }}
          y-button(label="Learn more" size="xs" button-class="px-3" height="28" @click="$emit('showUpgradeDialog')")
        .text-xl.font-semibold.mr-4 {{ $t('summary') }}
        v-divider.mt-4.mb-6
        v-row.mx-8(no-gutters)
          v-col(cols="2")
            .text-sm.neutral500--text {{ $t('patient') }}
          v-col(cols="4")
            .d-flex
              .mr-2
                v-img.round-4xl(v-if="patient.profile_image" width="20" height="20" :src="patient.profile_image.file" cover)
                  template(v-slot:placeholder)
                    v-row(no-gutters class="fill-height ma-0" align="center" justify="center")
                      v-progress-circular(indeterminate size="15" color="grey" width="2")
                v-card.neutral300.fill-height.round-4xl(v-else flat width="20" height="20")
                  div.d-flex.align-center.justify-center.fill-height
                    v-icon(size="20" x-small) $yzi-user-03
              div
                .text-sm.font-medium {{ patient.name }}
                .text-xs.mt-1(v-if="patient.ic") {{ patient.ic }}
            .d-flex.mt-2
              .text-xxs.underline.font-semibold.clickable(v-if="hasConsultation && !isHq" @click="onOpenConsultationNotes") See consultation notes
          v-col(cols="6")
            billing-info(:billingInfo="billingInfo" :patient="patient" @updated="fetchInvoice")
            template(v-if="showOutstandingBills && outstandingInvoice")
              v-row.mt-4(no-gutters)
                v-col(cols="5")
                  .text-sm.neutral500--text Outstanding invoice
                v-col.pl-2
                  div
                    .text-xs.font-semibold.neutral700--text {{ outstandingInvoice.amount | displayPrice($currency) }}
                      |
                      span.ml-2.text-xs.neutral500--text ({{ outstandingInvoice.count > 1 ? `${outstandingInvoice.count} invoices` : `${outstandingInvoice.count} invoice` }})
                  v-menu
                    template(v-slot:activator="{ on, attrs }")
                      .text-xxs.neutral700--text.underline.font-semibold.clickable.mt-1(v-on="on") View details
                    v-list.pa-0
                      template(v-for="(result, resultIndex) in outstandingInvoice.results" )
                        v-divider(v-if="resultIndex !== 0")
                        v-list-item(@click="onOpenOutstandingBill(result)")
                          .d-flex.align-center.justify-space-between(style="width: 200px")
                            div.d-flex.flex-column.justify-center
                              .text-xs.neutral700--text.font-semibold {{ result.total_amount - result.total_paid | displayPrice }}
                              .text-xxs.neutral500--text {{ result.record_date | humanizeDate}} 
                            div.text-xs.font-semibold {{ '#' + result.rid }}
        v-divider.mt-8.mb-6
        template(v-if="Auth('root', 'subscribe_marketing')")
          v-row
            v-col(cols="12" xl="6")
              checkout-offers-card(:key="checkoutOfferCardKey" :customer="patient" :lead-id="invoice.lead" @updated="fetchInvoice")
            v-col(cols="12" xl="6")
              checkout-loyalty-card(:customer="patient")
          v-divider.mt-8.mb-6
        v-row.mb-4(no-gutters)
          v-btn.button-xs.mr-2(outlined color="primary" :disabled="disabledPrintAllLabel" :loading="isDownloading" @click="onDownloadAll")
            v-icon(left) $yzi-tag-02
            | {{ $t('print_all_item', { item: $t('labels').toLowerCase() }) }}
          v-btn.button-xs(outlined color="primary" @click="onEditInvoice")
            v-icon(left) $yzi-edit-05
            | {{ $t('edit_item', { item: $t('invoice').toLowerCase() }) }}
          v-spacer
          v-btn.button-xs(outlined color="primary" :loading="isLoadingDownloadInvoice" @click="onDownloadInvoice")
            v-icon(left) $yzi-printer
            | {{ $t('print_item', { item: $t('invoice').toLowerCase() }) }}
        medication-table(:key="medicationTableKey" :parentMedications="medications" @updated="onUpdateMedicationTable")
      v-col.pa-8.mb-16.preview-background(cols="6")
        .d-flex.align-start.mb-16.fill-height
          payment-panel-preview.payment-panel-preview(:invoice="invoice" :transactions.sync="transactions" @updated="fetchInvoice" @updatedOffer="handleUpdateOffer")
    v-footer.white(padless fixed style="z-index: 20;")
      .d-flex.flex-column.grow.mb-2
        v-divider
        .d-flex.align-center.mt-2.mx-2
          v-btn.button-md.underline(text color="primary" @click="onClose") {{ $t('close') }}
          v-spacer
          template(v-if="!isHq")
            v-menu(v-if="!isCompleted" offset-y top attach rounded="lg")
              template(v-slot:activator="{ on }")
                v-btn.button-md.px-4.mr-4(color="primary" outlined depressed :loading="isLoadingCallPatientIn" v-on="on")
                  | {{ $t('call_in') }}
                  v-icon.ml-2(size=20) $yzi-chevron-down
              v-list.py-0
                template(v-if="seats.length > 0")
                  template(v-for="(seat, seatIdx) in seats")
                    v-divider(v-if="seats !== 0")
                    v-list-item(@click="onCallPatientIn(seat)" :key="'action-' + seatIdx")
                      v-list-item-title.text-sm.basic800--text {{ seat.name }}
                template(v-else)
                  v-list-item
                    v-list-item-title.text-sm.neutral500--text.italic {{ $t('no_list_available') }}
            y-button(v-if="isCompleted" :label="$t('save_and_close')" size="md" :loading="isLoadingSaveAndClose" button-class="px-4" @click="onClose")
            y-button(v-else label="Complete visitation" size="md" :loading="isLoadingSaveAndClose" button-class="px-4" @click="checkSaveAndClose")

  .dialogs
    download-invoice-receipt-dialog(v-if="openSelectInvoiceDialog" :open.sync="openSelectInvoiceDialog" :documents="invoiceTemplates" documentType="invoice")
    complete-visit-confirmation-dialog(v-if="openConfirmationDialog" :open.sync="openConfirmationDialog" :loading="isLoadingSaveAndClose" @confirm="onSaveAndClose")
    interface-update-dialog(v-if="showInterfaceUpdateDialog" :open.sync="showInterfaceUpdateDialog" @learnMore="$emit('showUpgradeDialog')")
    y-dialog(v-if="openOutstandingInvoice" v-model="openOutstandingInvoice" :max-width="600" persistent fluid scrollable content-class="round-2xl")
      v-card
        v-card-text.pa-6
          div.d-flex.align-center
            div.text-xl.font-medium.neutral700--text Open invoice {{ `#${selectedOutstandingInvoice.rid}` }}
            v-spacer
            v-btn(text icon @click="closeOutstandingInvoice")
              v-icon(color="neutral700" :size="24") mdi-close
          div.text-md.neutral700--text.mt-4 You will access another invoice. Would you like to open it in this tab, or would you prefer to open a new tab instead?

        v-divider
        v-card-actions.pa-6
          v-row
            v-col
              y-button(label="This tab" color="neutral700" outlined size="md" block @click="openInvoiceThisTab")
            v-col
              y-button(label="New tab" text-color="neutral100" color="neutral700" size="md" block dark @click="openInvoiceNewTab")
</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import MedicationTable from '@/components/clinic/checkout/MedicationTable.vue'
import PaymentPanelPreview from '@/components/clinic/checkout/PaymentPanelPreview.vue'
import BillingInfo from '@/components/clinic/invoice/BillingInfo.vue'
import DownloadInvoiceReceiptDialog from '@/components/clinic/dialogs/DownloadInvoiceReceiptDialog.vue'
import CompleteVisitConfirmationDialog from '@/components/clinic/checkout/CompleteVisitConfirmationDialog.vue'
import CheckoutOffersCard from "@/components/clinic/checkout/marketing/CheckoutOffersCardOld.vue"
import CheckoutLoyaltyCard from "@/components/clinic/checkout/marketing/CheckoutLoyaltyCardOld.vue"
import InterfaceUpdateDialog from '@/components/clinic/dialogs/InterfaceUpdateDialog.vue'

const INTERFACE_NOTICE_KEY = 'yz_checkout_interface_notice_last_shown'

export default {
  components: {
    MedicationTable, PaymentPanelPreview, BillingInfo, DownloadInvoiceReceiptDialog, CompleteVisitConfirmationDialog,
    CheckoutOffersCard, CheckoutLoyaltyCard, InterfaceUpdateDialog
  },
  data () {
    return {
      isFetching: true,
      isLoadingCallPatientIn: false,
      isLoadingSaveAndClose: false,
      isLoadingDownloadInvoice: false,
      isPrinting: false,
      isDownloading: false,
      openSelectInvoiceDialog: false,
      openConfirmationDialog: false,
      openOutstandingInvoice: false,
      showInterfaceUpdateDialog: false,
      invoice: null,
      transactions: null,
      outstandingInvoice: null,
      selectedOutstandingInvoice: null,
      medicationTableKey: 0,
      invoiceTemplates: [],
      seats: [],
      saveAndClosePayload: {
        status: 'completed'
      },
      checkoutOfferCardKey: 0,
      reservations: [],
      preorders: []
    }
  },
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
      company: 'getCompany',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    leadPid() {
      return this.$route.params.pid
    },
    registration() {
      return this.invoice.registration
    },
    patient() {
      return this.invoice.customer
    },
    billingInfo() {
      return this.invoice.billing_address
    },
    medications() {
      return this.invoice.products
    },
    transactionParams() {
      return {
        company: this.company.id,
        pid: this.leadPid
      }
    },
    transactionHqParams() {
      return {
        company: this.hqSelectedCompany?.id,
        pid: this.leadPid
      }
    },
    isCompleted() {
      // For migrated data, always completed
      if (!this.registration) return true

      return this.registration.status === 'completed'
    },
    allMedicationItems () {
      return this.medications.filter(item => item.product.medication || item.bundles.filter(bundle => bundle.product.medication).length)
    },
    disabledPrintAllLabel () {
      return this.allMedicationItems.length === 0
    },
    isOTC () {
      // For migrated data, otc is false
      if (!this.registration) return false

      return this.registration?.code === 3
    },
    hasRegistration () {
      if (this.registration) return true

      return false
    },
    hasConsultation () {
      return !this.isOTC && this.hasRegistration
    },
    fetchInvoiceParams () {
      return {
        company: this.isHq ? this.hqSelectedCompany?.id : this.company.id,
        status: 'active',
        categories: 'invoice'
      }
    },
    showOutstandingBills () {
      if (this.company.genes.find(gene => gene.feature === 'show_outstanding_bill_in_checkout')) return true
      
      return false
    },
    totalPaid () {
      if (!this.invoice) {
        return 0
      }
      if (this.transactions.length === 0) {
        return 0
      }
      return this.transactions.reduce((sum, trans) => {
        sum += trans.amount

        return sum
      }, 0)
    },
    hasReservationsOrPreorders () {
      return this.reservations.length > 0 || this.preorders.length > 0
    },
    reservationPreorderBannerText () {
      const parts = []
      if (this.reservations.length > 0) {
        parts.push(`${this.reservations.length} reservation${this.reservations.length > 1 ? 's' : ''}`)
      }
      if (this.preorders.length > 0) {
        parts.push(`${this.preorders.length} pre-order${this.preorders.length > 1 ? 's' : ''}`)
      }
      return `This patient has an active ${parts.join(' & ')}. Switch to the new Dispensary module to proceed.`
    },
    totalBalance () {
      if (!this.invoice) {
        return 0
      }
      return this.invoice.total_amount - this.totalPaid
    },
  },
  async mounted () {
    try {
      this.isFetching = true
      let allFetching = [this.fetchInvoice(), this.fetchAvailableSeats()]
      if (!this.isHq) {
        allFetching.push(this.fetchTransactions())
      }
      await Promise.all([...allFetching])
      if (this.isHq) {
        await this.fetchTransactionsHq()
      }
      this.isFetching = false
      this.fetchReservationsAndPreorders()
      this.checkDispenseNote()
      this.checkShowInterfaceUpdate()
    } catch (error) {
      console.log(error)
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchInvoice() {
      const endpoint = this.isHq ? `/hq/invoices/${this.leadPid}` : `/invoices/${this.leadPid}`
      return this.$axios.get(endpoint).then(({ data }) => {
          this.invoice = data
          if (this.isHq) {
            this.$store.commit('hq/setSelectedCompany', data.company)
          }
          this.fetchOutstandingInvoices(data.customer.id)
        }
      )
    },
    fetchTransactions() {
      return this.$axios.get('/transactions', { params: this.transactionParams }).then(({ data }) => this.transactions = data.results)
    },
    fetchTransactionsHq() {
      return this.$axios.get('/hq/transactions', { params: this.transactionHqParams }).then(({ data }) => this.transactions = data.results)
    },
    fetchAvailableSeats () {
      this.isLoadingCallPatientIn = true
      let params = {
        status: 'active',
        type: 'room',
        fields: 'id,name',
        page_size: -1
      }
      return this.$axios.get(`/clinics/${this.company.slug}/facilities`, { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.seats = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoadingCallPatientIn = false
        })
    },
    fetchOutstandingInvoices (id) {
      let params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        paid: false,
        form_type: 'registration',
        customer: id,
        page_size: -1
      }
      this.$axios.get(`/invoices`, { params })
        .then(resp => {
          if (resp.data && resp.data.results.length > 0) {
            const results = resp.data.results.filter((result) => result.pid !== this.leadPid)
            if (results.length > 0) {
              this.outstandingInvoice = {
                amount: results.reduce((sum, a) => { return sum + (a.total_amount - a.total_paid) }, 0),
                count: results.length,
                results,
                id
              }
            }
          }
        })
        .catch(() => {})
    },
    onEditInvoice() {
      // this.$router.push(`/admin/clinic/invoice/${this.leadPid}`)
      let next = {
        name: this.isHq ? 'HQInvoice' : 'ClinicInvoice',
        params: {
          pid: this.leadPid,
          origin: this.isHq ? 'HQCheckout' : 'ClinicCheckout',
          data: { params: { pid: this.leadPid, origin: this.$route.params.origin, data: this.$route.params.data } }
        }
      }
      this.$router.push(next)
    },
    onClose() {
      // this.$router.back()
      let name = this.isHq ? 'HQPatientList' : 'ClinicRegistration'
      if (this.$route.params && this.$route.params.origin) name = this.$route.params.origin
      let next = { name }
      if (this.$route.params && this.$route.params.data && Object.keys(this.$route.params.data).length) next = { name, ...this.$route.params.data }
      this.$router.push(next)
    },
    onOpenConsultationNotes () {
      let next = {
        name: 'ClinicConsultationDetails',
        params: {
          pid: this.leadPid,
          origin: 'ClinicCheckout',
          data: { params: { pid: this.leadPid, origin: this.$route.params.origin, data: this.$route.params.data } }
        }
      }
      this.$router.push(next)
    },
    checkSaveAndClose () {
      if (this.totalBalance) {
        this.openConfirmationDialog = true
        return
      }
      this.onSaveAndClose()
    },
    async onCallPatientIn(seat) {
      this.isLoadingCallPatientIn = true
      try {
        await this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, { seat: seat?.id })
        await this.fetchInvoice()
      } catch (error) {
        const errorData = error.response?.data?.detail
        if (errorData && errorData.includes('currently occupied')) {
          this.showMessage(['error', `${seat?.value} is currently occupied`])
          return
        }
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingCallPatientIn = false
      }
    },
    async onSaveAndClose() {
      this.isLoadingSaveAndClose = true
      try {
        await this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, this.saveAndClosePayload)
        this.createActivityLog('complete_visit', 'Visit completed')
        this.onClose()
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingSaveAndClose = false
      }
    },
    async onUpdateMedicationTable () {
      await this.fetchInvoice()
      this.medicationTableKey++
    },
    async onDownloadInvoice () {
      try {
        this.isLoadingDownloadInvoice = true
        const { data } = await this.$axios.get('/document_templates', { params: this.fetchInvoiceParams })
        if (data.count > 1) {
          this.invoiceTemplates = data.results
          this.openSelectInvoiceDialog = true
          this.isLoadingDownloadInvoice = false
          return
        }
        if (data.count === 1) {
          this.onDownload(data.results[0])
          return
        }
        this.showMessage(['error', this.$t('error_message.create_item_first', { item: this.$t('invoice').toLowerCase() })])
        if (this.isHq) {
          return
        }
        this.$router.push('/admin/clinic/settings/document/templates')
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingDownloadInvoice = false
      }
    },
    async onDownload (item) {
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoadingDownloadInvoice = false
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const url = `${baseUrl}/invoices/${this.leadPid}/preview_document?document_template=${item.id}&key=${key}`
      window.open(url,'_blank')
      this.isLoadingDownloadInvoice = false
    },
    // async onPrintAll (type) {
    //   if (type === 'medication') {
    //     if (this.allMedicationItems.length === 0) {
    //       this.showMessage(['error', 'No medication label'])
    //       return
    //     }
    //     this.isPrinting = true
    //     try {
    //       await this.$axios.post(`/clinics/${this.company.slug}/registrations/${this.leadPid}/print_medication_labels`)
    //       this.showMessage(['success', this.$t('success_message.successfully_send_to_printer')])
    //     } catch (error) {
    //       const errorData = error.response?.data?.detail
    //       if (errorData && errorData === 'Default printer not set') {
    //         this.$router.push('/admin/clinic/settings/hardware/printers')
    //         this.showMessage(['error', 'Please set your default printer'])
    //         return
    //       } else {
    //         this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
    //       }
    //     } finally {
    //       this.isPrinting = false
    //     }
    //   }
    // },
    async onDownloadAll () {
      if (this.allMedicationItems.length === 0) {
        this.showMessage(['error', 'No medication label'])
        return
      }
      // Download process
      this.isDownloading = true
      // Get key first
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isDownloading = false
        return
      }
      // Open download url
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      const url = `${baseUrl}/clinics/${companySlug}/registrations/${this.leadPid}/preview_medication_labels?key=${key}`
      window.open(url, '_blank')
      this.isDownloading = false
    },
    onOpenOutstandingBill (invoice) {
      this.selectedOutstandingInvoice = invoice
      this.openOutstandingInvoice = true
    },
    openInvoiceThisTab () {
      this.$router.push(`/admin/clinic/checkout/${this.selectedOutstandingInvoice.pid}`)
      this.$router.go()
      this.closeOutstandingInvoice()
    },
    openInvoiceNewTab () {
      const link = this.$router.resolve(`/admin/clinic/checkout/${this.selectedOutstandingInvoice.pid}`)
      window.open(link.href, '_blank')
      this.closeOutstandingInvoice()
    },
    closeOutstandingInvoice () {
      this.openOutstandingInvoice = false
      this.selectedOutstandingInvoice = null
    },
    handleUpdateOffer () {
      this.fetchInvoice()
      this.checkoutOfferCardKey++
    },
    checkDispenseNote () {
      if (this.hasRegistration && this.registration?.checkout_note) {
        this.$emit('dispense', true)
      }
    },
    checkShowInterfaceUpdate () {
      const today = DateTime.local().toFormat('yyyy-LL-dd')
      if (localStorage.getItem(INTERFACE_NOTICE_KEY) === today) return
      this.showInterfaceUpdateDialog = true
      localStorage.setItem(INTERFACE_NOTICE_KEY, today)
    },
    async fetchReservationsAndPreorders () {
      try {
        const [reservationsResp, preordersResp] = await Promise.all([
          this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params: { patient: this.invoice.customer.id, status: 'active', page_size: -1 } }),
          this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params: { patient: this.invoice.customer.id, status: 'active', page_size: -1 } })
        ])
        if (reservationsResp.data?.results) this.reservations = reservationsResp.data.results
        if (preordersResp.data?.results) this.preorders = preordersResp.data.results
      } catch {
        // silently ignore
      }
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: 'checkout',
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.preview-background {
  background-color: #F9FAFB;
}
::v-deep .theme--light.v-skeleton-loader .v-skeleton-loader__list-item-three-line {
  background: transparent !important;
}
.row-height {
  height:80vh;
}
.payment-panel-preview {
  position: sticky;
  top: 80px;
}
</style>